import { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./auth/AuthContext.js";
import { LoginPage } from "./auth/LoginPage.js";
import { RegisterPage } from "./auth/RegisterPage.js";
import { AppShell } from "./layout/AppShell.js";
import { ChatPage } from "./chat/ChatPage.js";
import { AgentListPage } from "./agents/AgentListPage.js";
import { AgentDetailPage } from "./agents/AgentDetailPage.js";
import { WorkflowListPage } from "./workflows/WorkflowListPage.js";
import { DAGEditor } from "./workflows/DAGEditor.js";
import { RunViewer } from "./workflows/RunViewer.js";
import { SkillsPage } from "./skills/SkillsPage.js";
import { ToolMarketplace } from "./tools/ToolMarketplace.js";
import { AgentCatalog } from "./agents/AgentCatalog.js";
import { WorkflowTemplateGallery } from "./workflows/WorkflowTemplateGallery.js";
import { PlannerPage } from "./planner/PlannerPage.js";
import { ChannelManager } from "./channels/ChannelManager.js";
import { AuditPage } from "./audit/AuditPage.js";
import { ActivityPage } from "./activity/ActivityPage.js";
import { OnboardingWizard, shouldShowOnboarding } from "./onboarding/OnboardingWizard.js";
import { AdminDashboard } from "./admin/AdminDashboard.js";
import { UserManager } from "./admin/UserManager.js";
import { ProviderHealth } from "./admin/ProviderHealth.js";
import { UsageChart } from "./admin/UsageChart.js";

export function App() {
  const { isAuthenticated, login } = useAuth();
  const [authView, setAuthView] = useState<"login" | "register">("login");
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Handle OAuth callback — tokens are passed as URL query parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthToken = params.get("oauth_token");
    const oauthRefresh = params.get("oauth_refresh");
    const oauthUser = params.get("oauth_user");

    if (oauthToken && oauthRefresh && oauthUser) {
      try {
        const user = JSON.parse(oauthUser);
        login(oauthToken, oauthRefresh, user);
      } catch {
        // Invalid user JSON — ignore
      }
      // Clean the URL
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [login]);

  // Check for first-login onboarding after authentication
  useEffect(() => {
    if (isAuthenticated) {
      shouldShowOnboarding().then((show) => setShowOnboarding(show));
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return authView === "register" ? (
      <RegisterPage onSwitchToLogin={() => setAuthView("login")} />
    ) : (
      <LoginPage onSwitchToRegister={() => setAuthView("register")} />
    );
  }

  return (
    <>
      {showOnboarding && (
        <OnboardingWizard
          onComplete={() => setShowOnboarding(false)}
          onSkip={() => setShowOnboarding(false)}
        />
      )}
      <Routes>
      <Route element={<AppShell />}>
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/chat/:conversationId" element={<ChatPage />} />
        <Route path="/agents" element={<AgentListPage />} />
        <Route path="/agents/catalog" element={<AgentCatalog />} />
        <Route path="/agents/:agentId" element={<AgentDetailPage />} />
        <Route path="/workflows" element={<WorkflowListPage />} />
        <Route path="/workflows/templates" element={<WorkflowTemplateGallery />} />
        <Route path="/workflows/:workflowId/edit" element={<DAGEditor />} />
        <Route path="/workflows/:workflowId/runs/:runId" element={<RunViewer />} />
        <Route path="/skills" element={<SkillsPage />} />
        <Route path="/tools" element={<ToolMarketplace />} />
        <Route path="/planner" element={<PlannerPage />} />
        <Route path="/channels" element={<ChannelManager />} />
        <Route path="/activity" element={<ActivityPage />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/users" element={<UserManager />} />
        <Route path="/admin/providers" element={<ProviderHealth />} />
        <Route path="/admin/usage" element={<UsageChart />} />
        <Route path="*" element={<Navigate to="/chat" replace />} />
      </Route>
    </Routes>
    </>
  );
}
